import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Stack,
  TextTitle,
  TextBody,
  Icon,
  Search,
  Spacer,
  Avatar,
  Tag,
  IconButton,
  Spinner,
  TabGroup,
  Tab,
  BlockList,
  ResourceCard,
} from '@pypestream/design-system';
import { urqlGql, VoiceAiProvider } from '@pypestream/api-services';
import debounce from 'lodash.debounce';
import InfiniteScroll from 'react-infinite-scroll-component';

import { GenericModal, GenericModalProps } from '..';
import { useAudioPlayback } from '../../../hooks/use-audio-playback';

import './voice-selector-modal.scss';

export interface VoiceSelectorModalProps extends GenericModalProps {
  selectedValues: {
    provider: string;
    audioModel: string;
    elevenlabsVoiceId: string;
    elevenlabsSimilarity: number;
    elevenlabsSpeed: number;
    elevenlabsStability: number;
  };
  onVoiceSelect?: (voiceId: string, voiceMetadata: VoiceAiProvider) => void;
}

export const VoiceSelectorModal: FC<VoiceSelectorModalProps> = ({
  modalRef,
  onClose,
  selectedValues,
  onVoiceSelect,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVoiceId, setSelectedVoiceId] = useState<string>('');
  const [genderFilter, setGenderFilter] = useState<'all' | 'male' | 'female'>(
    'all'
  );
  const [allVoices, setAllVoices] = useState<VoiceAiProvider[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const { playAudio, isPlaying } = useAudioPlayback();

  const prevAudioModel = useRef(selectedValues.audioModel);
  const prevGenderFilter = useRef(genderFilter);
  const prevSearchTerm = useRef(searchTerm);
  const isInitialMount = useRef(true);

  const getVoiceInitials = (name: string): string => {
    if (!name) return '';

    const firstWord = name.split(/[-,\s]/)[0];

    if (firstWord.length <= 2) {
      return firstWord.toUpperCase();
    }

    return firstWord.substring(0, 2).toUpperCase();
  };

  const getElevenLabsData = useCallback(
    async (page: number = 0, append: boolean = false, search: string) => {
      if (!selectedValues.audioModel) {
        return;
      }

      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }

      try {
        const response = await urqlGql.getVoiceAiProviders({
          args: {
            locale: 'en-US',
            provider: 'ElevenLabs',
            accent: 'american',
            gender: genderFilter === 'all' ? '' : genderFilter,
            language: 'en',
            modelId: selectedValues.audioModel,
            search,
            page: page,
            pageSize: 30,
          },
        });

        const newData = response.data?.getVoiceAiProviders || {};
        const newVoices = newData.voices || [];

        if (append) {
          setAllVoices((prev) => [...prev, ...newVoices]);
        } else {
          setAllVoices(newVoices);
        }
        setHasMoreData(newData.has_more || false);
        setCurrentPage(page);
      } catch (error) {
        console.error('Error fetching voices:', error);
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [selectedValues.audioModel, genderFilter]
  );

  const resetPagination = useCallback(() => {
    setCurrentPage(0);
    setAllVoices([]);
    setHasMoreData(true);
  }, []);

  const debouncedSearch = useMemo(
    () =>
      debounce((search: string) => {
        resetPagination();
        getElevenLabsData(0, false, search);
      }, 500),
    [resetPagination, getElevenLabsData]
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useEffect(() => {
    if (!selectedValues.audioModel) return;

    const audioModelChanged =
      prevAudioModel.current !== selectedValues.audioModel;
    const genderFilterChanged = prevGenderFilter.current !== genderFilter;

    prevAudioModel.current = selectedValues.audioModel;
    prevGenderFilter.current = genderFilter;

    if (isInitialMount.current || audioModelChanged || genderFilterChanged) {
      isInitialMount.current = false;
      debouncedSearch.cancel();
      resetPagination();
      getElevenLabsData(0, false, searchTerm);
    }
  }, [
    selectedValues.audioModel,
    genderFilter,
    resetPagination,
    getElevenLabsData,
    searchTerm,
    debouncedSearch,
  ]);

  useEffect(() => {
    if (!selectedValues.audioModel || isInitialMount.current) return;

    const searchTermChanged = prevSearchTerm.current !== searchTerm;
    prevSearchTerm.current = searchTerm;

    if (!searchTermChanged) return;

    if (searchTerm !== '') {
      debouncedSearch(searchTerm);
    } else {
      debouncedSearch.cancel();
      resetPagination();
      getElevenLabsData(0, false, '');
    }
  }, [
    searchTerm,
    selectedValues.audioModel,
    debouncedSearch,
    resetPagination,
    getElevenLabsData,
  ]);

  const loadMoreData = useCallback(() => {
    if (hasMoreData && !isLoadingMore) {
      getElevenLabsData(currentPage + 1, true, searchTerm);
    }
  }, [hasMoreData, isLoadingMore, currentPage, getElevenLabsData, searchTerm]);

  const handleSave = useCallback(() => {
    const selectedVoice = allVoices?.find(
      (voice) => voice?.voice_id === selectedVoiceId
    );
    if (selectedVoiceId && selectedVoice?.voice_id) {
      onVoiceSelect?.(selectedVoice?.voice_id, selectedVoice);
    }
    onClose?.();
  }, [allVoices, selectedVoiceId, onVoiceSelect, onClose]);

  return (
    <GenericModal
      modalRef={modalRef}
      onClose={onClose}
      size="large"
      test-id="voice-selector-modal"
    >
      <TextTitle size="small">Choose a voice</TextTitle>
      <Spacer size="large" />
      <Stack gutter="medium" direction="row" nowrap>
        <Search
          placeholder="Search voices..."
          value={searchTerm}
          onInput={(e) => setSearchTerm(e.target.value)}
          className="c-voice-selector-modal__search"
          size="large"
        />
        <TabGroup variant="flush" className="c-voice-selector-modal__tabs">
          <Tab slot="tab" panel="all" onClick={() => setGenderFilter('all')}>
            All
          </Tab>
          <Tab slot="tab" panel="male" onClick={() => setGenderFilter('male')}>
            Male
          </Tab>
          <Tab
            slot="tab"
            panel="female"
            onClick={() => setGenderFilter('female')}
          >
            Female
          </Tab>
        </TabGroup>
      </Stack>
      <Spacer size="xsmall" />
      <div id="voice-scroll-container" className="c-voice-selector-modal__list">
        {isLoading ? (
          <div className="c-voice-selector-modal__info c-voice-selector-modal__info--full-height">
            <Spinner />
            <Spacer size="xsmall" />
            <TextBody size="small" textAlign="center">
              Loading voices...
            </TextBody>
          </div>
        ) : (
          <InfiniteScroll
            dataLength={allVoices.length}
            next={loadMoreData}
            hasMore={hasMoreData}
            loader={
              <div className="c-voice-selector-modal__info">
                <Spinner />
                <Spacer size="xsmall" />
                <TextBody size="small" textAlign="center">
                  Loading more voices...
                </TextBody>
              </div>
            }
            endMessage={
              <div className="c-voice-selector-modal__info">
                <TextBody size="small" textAlign="center">
                  No more voices to load
                </TextBody>
              </div>
            }
            scrollableTarget="voice-scroll-container"
          >
            <BlockList gutter="xsmall">
              {allVoices.length > 0 ? (
                allVoices.map((voice, index) => {
                  const sound = voice?.preview_url;

                  return (
                    <ResourceCard
                      key={`${voice?.voice_id}-${index}`}
                      interactive
                      selected={selectedVoiceId === voice?.voice_id}
                      onClick={() => {
                        setSelectedVoiceId(voice?.voice_id || '');
                      }}
                      label={voice?.name || ''}
                      description={voice?.description || undefined}
                    >
                      <Avatar
                        label={getVoiceInitials(voice?.name || '')}
                        src={voice?.image_url || ''}
                        size="small"
                        slot="label-prefix"
                      />
                      <Tag variant="default" minimal slot="label-suffix">
                        {voice?.accent || 'no accent'}
                      </Tag>
                      <IconButton
                        slot="action"
                        variant="subtle"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (sound) {
                            playAudio(voice.voice_id || '', sound);
                          }
                        }}
                      >
                        <Icon
                          name={
                            isPlaying(voice?.voice_id || '')
                              ? 'recording'
                              : 'play'
                          }
                          size="xsmall"
                          weight="medium"
                        />
                      </IconButton>
                    </ResourceCard>
                  );
                })
              ) : (
                <div className="c-voice-selector-modal__info">
                  <TextBody textAlign="center">No voices found</TextBody>
                  <TextBody variant="secondary" textAlign="center">
                    Try adjusting your search terms
                  </TextBody>
                </div>
              )}
            </BlockList>
          </InfiniteScroll>
        )}
      </div>

      <div slot="footer">
        <Stack justifyContent="end" gutter="small">
          <Button
            size="large"
            variant="secondary"
            onClick={onClose}
            test-id="ps-voice-selector-cancel-button"
          >
            Cancel
          </Button>
          <Button
            size="large"
            onClick={handleSave}
            disabled={!selectedVoiceId}
            test-id="ps-voice-selector-confirm-button"
          >
            Confirm
          </Button>
        </Stack>
      </div>
    </GenericModal>
  );
};
